import { Download } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ERLAUTERUNG_IN_KOSTEN_TITLE,
	FORM_FIELDS_TYPES,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	PARTNER_DATEN_NICHT_ERFASST,
	PLATZHALTER_TEXT,
} from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { EmailValidation, ForceTrue, GetTextboxValidation, NonRequired, PhoneNumberValidation, Required } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getBasisDataKostenKategorie,
	getKostenErlauterung,
	getSelectedKostenKategorieBezeichnung,
} from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { checkIfSpecificKostenPositionHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { DownloadMitteilungAnderZuwendungsgeber } from 'src/pages/private/online-antrag/DownloadAntragPDF';
import { mapCustomFormStrucIndex } from 'src/utils/formStruc';
import { addIndexesToPath, addIndexToPath, checkReadOnlyInUeberarbeitung, getOptionsFromRes, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import EinnahmenHinweisGlobal from '../../../../global-structure/einnahmen-hinweis/content/EinnahmenHinweisGlobal';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const EinnahmenAntragKreativwirtschaft: FC<{ kostentraeger: string; unternehmenId?: number; hasComment?: boolean }> = ({
	kostentraeger,
	unternehmenId,
	hasComment,
}) => {
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const [kostenKategorie, setKostenKategorie] = useState([]);
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleDownload = (id: number) => {
		DownloadMitteilungAnderZuwendungsgeber(application, readOnly, saveApplication, id);
	};
	const handleAddEinnahmen = async () => {
		let appCopy = cloneDeep(application);

		const data = getBasisDataKostenKategorie(
			appCopy,
			kostenKategorie, // All kostenkategorie for Sachkosten, to find default Kostenkategorie id
			kostentraeger,
			unternehmenId,
			KOSTEN_POSITION.Einnahme,
			KOSTEN_ARTEN.Eigenmittel,
		);
		set(data, 'Mitteilung_Anderer_Einrichtungen', {
			Fragen_Block_1: [{ Zelle_1: '' }],
		});
		set(
			appCopy,
			addIndexesToPath(
				P.Foerderantrag_Kostenpositions.path,
				[`${get(application, P.Foerderantrag_Kostenpositions.path, []).length}`],
				['Foerderantrag_Kostenpositions'],
			),
			{ attributes: data },
		);
		saveApplication(false, appCopy);
	};
	const handleDeleteEinnahmen = async (id: number) => {
		try {
			let appcopy = cloneDeep(application);
			const allKostenposition = get(appcopy, P.Foerderantrag_Kostenpositions.path, []);
			set(
				appcopy,
				P.Foerderantrag_Kostenpositions.path,
				allKostenposition.filter((item: any) => item.id !== id),
			);
			let res = await DeleteRecord(APIS.Kostenposition, id);
			if (res) {
				saveApplication(false, appcopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Sachkosten', err);
		}
	};
	// Fetch KostenKategories
	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = withKostenkategorieEinsatzbereichFilter({
					Allgemein_Kostenposition: KOSTEN_POSITION.Einnahme,
					Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Eigenmittel,
					Allgemein_Antragsformular: { $contains: get(application, P.Allgemein_Antragsformular.path) },
					Allgemein_Aktiv: { $eq: true },
				}, APIS.Antrag);
				let res = await GetRecords(APIS.Kostenkategorie, [], filter);
				setKostenKategorie(removeNullDeep(removeDataKeys(get(res, 'data'))));
			} catch (err: any) {
				console.error('ERROR: Fetching Kostenkategorie ', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Eigen- und Drittmittel' description={PLATZHALTER_TEXT} />
			<EinnahmenHinweisGlobal kostentraeger={kostentraeger} unternehmenId={unternehmenId} />

			{/* eslint-disable-next-line array-callback-return */}
			{get(application, P.Foerderantrag_Kostenpositions.path, []).map((ele: any, i: number) => {
				if (
					get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme &&
					get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Eigenmittel &&
					get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId
				) {
					const selectedKostenKategorieName = getSelectedKostenKategorieBezeichnung(
						kostenKategorie,
						get(ele, KP_P.Allgemein_Kostenkategorie.path),
					);

					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={`Eigen- und Drittmittel ${selectedKostenKategorieName ? '| ' + selectedKostenKategorieName : ''} ${get(ele, KP_P.Allgemein_Bezeichnung.path, '') ? '| ' + get(ele, KP_P.Allgemein_Bezeichnung.path, '') : ''}`}
							onDelete={() => handleDeleteEinnahmen(get(ele, 'id'))}
							showDeleteButton={!updatedReadOnly}
							hasComment={checkIfSpecificKostenPositionHasComment(readOnly, ele)}
						>
							<PruefAssistenz
								statusPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Pruefstatus.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								hinweisPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Hinweis_Fuer_Antragsteller.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								ueberarbeitetAmPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								erledigtPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								formStruc={mapKosten(formStrucPruefAssistenz(), i)}
							/>

							<MUIAlert
								title={ERLAUTERUNG_IN_KOSTEN_TITLE}
								message={getKostenErlauterung(kostenKategorie, get(ele, KP_P.Allgemein_Kostenkategorie.path))}
							/>
							<Grid2 spacing={6} container>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapKosten(formStrucSpalte1(kostenKategorie), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
										size='small'
									/>
								</Grid2>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapKosten(formStrucSpalte2(), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
										size='small'
									/>
								</Grid2>
							</Grid2>

							{getSelectedKostenKategorieBezeichnung(kostenKategorie, get(ele, KP_P.Allgemein_Kostenkategorie.path)) === 'Öffentliche Mittel' ?
								<>
									<InhaltTrenner />
									<InhaltWrapper>
										<Typography variant='h4' component={'h2'}>
											Mitteilung andere Zuwendungsgeber
										</Typography>
										<Typography>
											Bitte geben hier an, falls Sie noch eine weitere Zuwendung einer anderen Einrichtung für Ihre geplante Veranstaltung
											erhalten werden. Dabei geht es nicht um Sponsoring, sondern um eine Förderung im Sinne des Haushaltsrechts, auf Basis
											einer Förderrichtlinie. Im Falle einer weiteren Zuwendung muss die Hessen Agentur als Bewilligungsbehörde mit der
											anderen Einrichtung eine Vereinbarung zum Einvernehmen treffen, um Transparenz über die Gesamtförderung herzustellen.
											Das besagt die folgende Regelung der Landeshaushaltsordnung:
										</Typography>
										<Typography sx={{ fontWeight: 500 }}>Nr. 1.4 VV zu § 44 LHO</Typography>
										<Typography>
											„Sollen für denselben Zweck Zuwendungen ausnahmsweise von mehreren Stellen des Landes oder sowohl vom Land als auch
											von anderen juristischen Personen des öffentlichen Rechts bewilligt werden, haben die Zuwendungsgeber vor der
											Bewilligung mindestens Einvernehmen herbeizuführen.“
										</Typography>
										<Typography sx={{ fontWeight: 500 }}>
											Bitte stimmen Sie sich mit den betreffenden Ansprechpartnern bzw. Ansprechpartnerinnen eng ab, um die folgenden
											Fragen richtig zu beantworten:
										</Typography>
									</InhaltWrapper>

									<CreateForm
										formStruc={mapKosten(formStrucMitteilungFelder(application, i), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
										size='small'
									/>

									<InhaltWrapper>
										<Typography>
											Wenn die Fragen A oder B und C und D mit „Ja“ beantwortet werden, muss die Hessen Agentur vor einer möglichen
											Bewilligung des Vorhabens ein Einvernehmen mit den/dem anderen Zuwendungsgeber/n herbeiführen und einen
											entsprechenden Vertrag schließen.
										</Typography>
										<Typography>Dafür werden folgende Angaben benötigt:</Typography>
									</InhaltWrapper>

									<CreateForm
										formStruc={mapKosten(formStrucMitteilungZuwenundungsgeber(application, i), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={updatedReadOnly}
										api={APIS.Kostenposition}
										size='small'
									/>
									<InhaltTrenner />
									<InhaltWrapper>
										<Typography variant='h4' component={'h2'}>
											Mitteilung anderer Zuwendungsgeber einreichen
										</Typography>
										<Typography>
											Laden Sie hier das für Sie vorbereitete Dokument „Mitteilung anderer Zuwendungsgeber“ herunter und unterschreiben Sie
											es:
										</Typography>
										<Button
											variant={'outlined'}
											startIcon={<Download />}
											sx={{ maxWidth: 'fit-content' }}
											onClick={() => handleDownload(get(ele, 'id'))}
										>
											Mitteilung anderer Zuwendungsgeber
										</Button>
									</InhaltWrapper>

									<InhaltTrenner marginTop='2rem' />

									<InhaltWrapper>
										<Typography sx={{ fontWeight: '500' }}>
											Bitte laden Sie hier Ihre unterschriebene Mitteilung anderer Zuwendungsgeber als pdf-Dokument hoch:
										</Typography>
									</InhaltWrapper>

									<Box sx={{ mt: '1rem' }}>
										<CreateForm
											formStruc={mapKosten(formStrucMitteilungUploadFelder(), i)}
											formValues={application}
											updateFormValuesFunc={setApplicationProperty}
											readOnly={updatedReadOnly}
											api={APIS.Kostenposition}
											size='small'
										/>
									</Box>
								</>
							:	null}
						</MUIAccordionComponent>
					);
				}
			})}
			{unternehmenId ?
				<Button sx={{ mt: '2rem', maxWidth: 'fit-content' }} endIcon={<AddIcon />} onClick={handleAddEinnahmen} disabled={updatedReadOnly}>
					Eigen- und Drittmittel hinzufügen
				</Button>
			:	<MUIAlert message={PARTNER_DATEN_NICHT_ERFASST} />}
		</>
	);
};

export default EinnahmenAntragKreativwirtschaft;

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};
const formStrucSpalte1 = (kostenKategories?: any[]) => {
	const kategorieOptions = kostenKategories || [];
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie,
					label: 'Kategorie',
					type: 'enumeration',
					options: getOptionsFromRes(kategorieOptions, 'Allgemein_Bezeichnung'),
				},
				{ ...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Bezeichnung, validation: [{ validateFunction: NonRequired }] },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucSpalte2 = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme, format: 'currency' }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

// erstmal nur auskommentieren.
// WEil TW wird bestimmt das 100 Mal ändern wollen
// Dass die Felder DYNAMISCH sind
const formStrucMitteilungFelder = (_application: any, _index: number) => {
	// const pathFrage_C = addIndexesToPath(
	// 	P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes
	// 		.Mitteilung_Ueber_Die_Gew_0262747250_en_Benoetigt_Block_3_Zelle_2.path,
	// 	[`${index}`],
	// 	['Foerderantrag_Kostenpositions']
	// );
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes
						.Mitteilung_Ueber_Die_Gew_0262747282_en_Benoetigt_Block_1_Zelle_2,
					label: '(A) Werden für den gleichen Zuwendungszweck bzw. für das o.g. Projekt Fördermittel bei anderen als der Hessen Agentur beantragt?',
					type: FORM_FIELDS_TYPES.radio,
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes
						.Mitteilung_Ueber_Die_Gew_0262747298_en_Benoetigt_Block_2_Zelle_2,
					label: '(B) Werden für den gleichen Zuwendungszweck bzw. für das o.g. Projekt Fördermittel von anderen als der Hessen Agentur gewährt?',
					type: FORM_FIELDS_TYPES.radio,
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes
						.Mitteilung_Ueber_Die_Gew_0262747250_en_Benoetigt_Block_3_Zelle_2,
					label: '(C) Handelt es sich bei einem anderen um einen Zuwendungsgeber im Sinne der Nr. 1.4 VV zu § 44 LHO?',
					type: FORM_FIELDS_TYPES.radio,
				},
				// ...(isTrue(get(application, pathFrage_C)) ?
				// 	[
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes
						.Mitteilung_Ueber_Die_Gew_0262747522_en_Benoetigt_Block_4_Zelle_2,
					label: '(D) Wenn Frage C mit „Ja“ beantwortet wird: Handelt es sich dabei um eine Zuwendung?',
					type: FORM_FIELDS_TYPES.radio,
				},
				// 	]
				// :	[]),
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];

	return struc;
};

const formStrucMitteilungZuwenundungsgeber = (_application: any, _index: number) => {
	// const pathFrage_A = addIndexesToPath(
	// 	P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes
	// 		.Mitteilung_Ueber_Die_Gew_0262747282_en_Benoetigt_Block_1_Zelle_2.path,
	// 	[`${index}`],
	// 	['Foerderantrag_Kostenpositions']
	// );
	// const pathFrage_B = addIndexesToPath(
	// 	P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes
	// 		.Mitteilung_Ueber_Die_Gew_0262747298_en_Benoetigt_Block_2_Zelle_2.path,
	// 	[`${index}`],
	// 	['Foerderantrag_Kostenpositions']
	// );
	// const pathFrage_C = addIndexesToPath(
	// 	P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes
	// 		.Mitteilung_Ueber_Die_Gew_0262747250_en_Benoetigt_Block_3_Zelle_2.path,
	// 	[`${index}`],
	// 	['Foerderantrag_Kostenpositions']
	// );
	// const pathFrage_D = addIndexesToPath(
	// 	P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes
	// 		.Mitteilung_Ueber_Die_Gew_0262747522_en_Benoetigt_Block_4_Zelle_2.path,
	// 	[`${index}`],
	// 	['Foerderantrag_Kostenpositions']
	// );

	let struc: FormStrucItem[] = [];

	// if (
	// 	isTrue(get(application, pathFrage_A)) ||
	// 	isTrue(get(application, pathFrage_B)) ||
	// 	isTrue(get(application, pathFrage_C)) ||
	// 	isTrue(get(application, pathFrage_D))
	// ) {
	struc = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes.Fragen_Block_1,
					type: FORM_FIELDS_TYPES.componentTable,
					fieldProps: {
						Zelle_1: { label: 'Name des/der anderen Zuwendungsgeber/s' },
						Zelle_2: { label: 'Anschrift des/der anderen Zuwendungsgeber/s', maxCharLength: 2000 },
						Zelle_3: { label: 'Ansprechperson', width: '11rem' },
						Zelle_4: { width: '11rem' },
						Zelle_5: { width: '11rem' },
						Zelle_6: { width: '9rem' },
					},
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	// }

	return struc;
};

const customFormStrucMitteilungAndererEinrichtungen = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes.Fragen_Block_1.schema
						.attributes.Zelle_1,
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes.Fragen_Block_1.schema
						.attributes.Zelle_2,
					validation: [{ validateFunction: Required }, { validateFunction: GetTextboxValidation(2000) }],
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes.Fragen_Block_1.schema
						.attributes.Zelle_3,
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes.Fragen_Block_1.schema
						.attributes.Zelle_4,
					validation: [{ validateFunction: Required }, { validateFunction: PhoneNumberValidation }],
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes.Fragen_Block_1.schema
						.attributes.Zelle_5,
					validation: [{ validateFunction: Required }, { validateFunction: EmailValidation }],
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes.Fragen_Block_1.schema
						.attributes.Zelle_6,
				},
			],
		},
	];

	return struc;
};

const formStrucMitteilungUploadFelder = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Mitteilung_Anderer_Zuwendungsgeber_Upload, hideLabel: true }],
			userProps: { direction: 'column', columnToDivide: 2 },
		},
	];

	return struc;
};

const mapKosten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Foerderantrag_Kostenpositions.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Foerderantrag_Kostenpositions.path.length),
				};
			}),
		};
	});
	return result;
};

export const getFormStrucEinnahmenAntragKreativwirtschaft = (application: any, kostenKategorie: any[], unternehmenId: number) => {
	let result: FormStrucItem[] = [];

	get(application, P.Foerderantrag_Kostenpositions.path, []).forEach((ele: any, i: number) => {
		if (
			get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme &&
			get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Eigenmittel &&
			get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId
		) {
			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(ele, KP_P.Pruefung_Pruefstatus.path, KP_P.Pruefung_Erledigt.path);

			const parentObject = {
				...P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes.Fragen_Block_1,
				path: addIndexToPath(
					P.Foerderantrag_Kostenpositions.schema.attributes.Mitteilung_Anderer_Einrichtungen.schema.attributes.Fragen_Block_1.path,
					i,
					P.Foerderantrag_Kostenpositions.path,
				),
			};
			const formStrucSpalte1WithIndex = mapKosten(formStrucSpalte1(), i);
			const formStrucSpalte2WithIndex = mapKosten(formStrucSpalte2(), i);

			let formStrucMitteilungFelderWithIndex = [];
			let formStrucUploadWithIndex = [];
			let customFormStrucMitteilungAndererEinrichtungenWithParentIndex: FormStrucItem[] = [];

			if (getSelectedKostenKategorieBezeichnung(kostenKategorie, get(ele, KP_P.Allgemein_Kostenkategorie.path)) === 'Öffentliche Mittel') {
				formStrucMitteilungFelderWithIndex = mapKosten(formStrucMitteilungFelder(application, i), i);
				customFormStrucMitteilungAndererEinrichtungenWithParentIndex = mapCustomFormStrucIndex(
					application,
					customFormStrucMitteilungAndererEinrichtungen(),
					parentObject,
				);
				formStrucUploadWithIndex = mapKosten(formStrucMitteilungUploadFelder(), i);
			}
			if (isHinweisFieldRequired) {
				result = concat(
					mapKosten(formStrucPruefAssistenz(), i),
					result,
					formStrucSpalte1WithIndex,
					formStrucSpalte2WithIndex,
					formStrucMitteilungFelderWithIndex,
					customFormStrucMitteilungAndererEinrichtungenWithParentIndex,
					formStrucUploadWithIndex,
				);
			} else {
				result = concat(
					result,
					formStrucSpalte1WithIndex,
					formStrucSpalte2WithIndex,
					formStrucMitteilungFelderWithIndex,
					customFormStrucMitteilungAndererEinrichtungenWithParentIndex,
					formStrucUploadWithIndex,
				);
			}
		}
	});

	return result;
};
